import { useState, useCallback, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import _, { groupBy } from 'underscore';
import InsuranceApi from "../../../../api/insurance";

function Insurance() {
  const [formData, setFormData] = useState({ insurance: '' });
  const [insurances, setInsurances] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    InsuranceApi.getAvailableInsurances().then(response => {
      if (response && response.data) {
        setInsurances(response.data);
      }
    }).catch(error => {
      setError("Failed to fetch available insurances");
    });
  }, []);

  const handleChange = useCallback((event, value) => {
    setFormData({ insurance: value });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!formData.insurance.trim()) {
      setError("Please enter an insurance name");
      setChartData([]);
      return;
    }

    setLoading(true);
    InsuranceApi.getByInsurance(formData.insurance).then(async (response) => {
      setLoading(false);
      if (response && response.data) {
        if (response.data.length === 0) {
          setError("Insurance not found");
          setChartData([]);
        } else {
          const sets = groupBy(response.data, (d) => d.State);
          const newChartData = Object.keys(sets).map((key) => {
            const groupedByYear = groupBy(sets[key], (item) => item.Year_received);
            const chartData = Object.keys(groupedByYear).map((year) => {
              const dataPoints = groupedByYear[year].map((item) => parseFloat(item.Insurance_percentage) * 100);
              const averagePercentage = dataPoints.reduce((sum, value) => sum + value, 0) / dataPoints.length;
              return { year, averagePercentage: averagePercentage.toFixed(2) };
            });

            const allDataPoints = sets[key].map((item) => parseFloat(item.Insurance_percentage_no_year) * 100);
            const generalAveragePercentage = (allDataPoints.reduce((sum, value) => sum + value, 0) / allDataPoints.length).toFixed(2);

            const latestYear = Math.max(...Object.keys(groupedByYear).map(year => parseInt(year)));
            const latestYearGroupedByLOC = groupBy(groupedByYear[latestYear], (item) => item.LOC);
            const latestYearData = Object.keys(latestYearGroupedByLOC).map(loc => {
              const locDataPoints = latestYearGroupedByLOC[loc].map(item => parseFloat(item.Insurance_percentage) * 100);
              const averageLOCPercentage = locDataPoints.reduce((sum, value) => sum + value, 0) / locDataPoints.length;
              return {
                loc,
                percentage: averageLOCPercentage.toFixed(2)
              };
            });

            return {
              labels: chartData.map((data) => data.year),
              title: key,
              datasets: [
                {
                  label: key,
                  color: "dark",
                  data: chartData.map((data) => data.averagePercentage),
                },
              ],
              generalAveragePercentage,
              latestYearData,
            };
          });

          setChartData(newChartData.filter(chart => chart.datasets[0].data.length > 0));
          setError("");
        }
      }
    }).catch((error) => {
      setLoading(false);
      if (error.message) {
        setError(error.response.data.message);
      } else {
        setError("There has been an error.");
      }
      setChartData([]);
    });
  }, [formData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <Autocomplete
                    options={insurances}
                    getOptionLabel={(option) => option}
                    value={formData.insurance}
                    onChange={handleChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Insurance"
                        variant="outlined"
                      />
                    )}
                  />
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Search"}
                </Button>
              </Grid>
              {error && (
                <Grid item xs={12}>
                  <SoftBox mb={2} color="red">
                    {error}
                  </SoftBox>
                </Grid>
              )}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {chartData && chartData.length > 0 && chartData.map((currentDataChart, index) => (
                    <Grid item xs={6} key={index}>
                      <SoftBox mb={2}>
                        <VerticalBarChart
                          icon="date_range"
                          title={currentDataChart.title}
                          count={480}
                          progress={60}
                          chart={currentDataChart}
                        />
                        <SoftBox mt={2}>
                          <div>All years average: {currentDataChart.generalAveragePercentage}%</div>
                          <div>Latest Year Data:</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>LOC</th>
                                <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentDataChart.latestYearData.map((data, idx) => (
                                <tr key={idx}>
                                  <td style={{ borderRight: '1px solid #ddd', padding: '8px' }}>{data.loc}</td>
                                  <td style={{ padding: '8px' }}>{data.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SoftBox>
      <SoftBox mt={6}>
        <Divider />
        <Footer />
      </SoftBox>
    </DashboardLayout>
  );
}

export default Insurance;
